import React, { PropsWithChildren, useState } from "react";
import {
  Button,
  ButtonProps,
  InteractiveContainer,
  Menu,
  MenuSections,
  PlainButton,
  Tooltip,
} from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { SwitchProps } from "@jugl-web/ui-components/cross-platform/Switch";
import { CRMStatus, CRMStatusPill } from "../CRMStatusPill";
import { ReactComponent as MenuIcon } from "./assets/menu.svg";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";

export interface CRMAccountCardProps {
  title: string;
  description: React.ReactNode;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  status?: CRMStatus;
  isConnected: boolean;
  img?: string;
  defaultAvatar?: string;
  buttonProps?: Omit<ButtonProps, "ref"> & {
    text: string;
  };
  switchProps?: SwitchProps;
  menuItems?: MenuSections;
  onClick?: () => void;
}

export const CRMAccountTile: React.FC<
  PropsWithChildren<CRMAccountCardProps>
> = ({
  img,
  defaultAvatar,
  title,
  description,
  status,
  icon,
  buttonProps,
  isConnected,
  menuItems,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { t } = useTranslations();
  return (
    <InteractiveContainer
      className={cx(
        "bg-grey-100 hover:border-dark-200 relative flex h-[380px] w-[316px] flex-col items-center overflow-hidden rounded-3xl border-transparent transition-all duration-300 hover:border-[1px] hover:border-solid hover:shadow-[0_8px_8px_0_rgba(0,0,0,0.12)]",
        {
          "border-grey-300 border-[1px] border-solid bg-white shadow-[0_4px_4px_0_rgba(0,0,0,0.08)]":
            isConnected,
          "pt-[64px]": isHovered,
          "pt-[85.5px]": !isHovered,
        }
      )}
      onClick={onClick}
      onMouseEnter={() => !isConnected && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative">
        {img && (
          <div className="border-0.5 flex h-[70px] w-[70px] shrink-0 items-center justify-center overflow-hidden rounded-2xl border-solid border-white bg-[#E0E0E0] shadow-[-8px_8px_16px_0_rgba(87,68,111,0.16)]">
            <img
              src={img}
              alt={title}
              className="h-[70px] w-[70px] object-cover object-center"
            />
          </div>
        )}
        {!img && (
          <div className="flex h-[70px] w-[70px] rounded-2xl shadow-[-8px_8px_16px_0_rgba(87,68,111,0.16)]">
            <img
              src={defaultAvatar}
              alt={title}
              className="h-full w-full object-cover object-center"
            />
          </div>
        )}
        <div className="absolute -bottom-2.5 -right-2.5">
          {React.createElement(icon)}
        </div>
      </div>
      <div className="flex flex-col gap-1 overflow-hidden pt-5 text-center">
        <span className="font-secondary truncate text-lg font-medium leading-5">
          {title}
        </span>
        <span className="text-dark-700 font-secondary px-7 text-sm leading-[140%]">
          {description}
        </span>
      </div>
      <div
        className={cx(
          "absolute bottom-[30px] left-0 right-0 flex justify-center gap-5 transition-all duration-500",
          {
            "bottom-[-50px]": !isHovered,
          }
        )}
      >
        <Tooltip
          renderTrigger={({ ref, props }) => {
            return (
              <PlainButton
                ref={ref}
                {...props}
                className="bg-primary hover:bg-primary-600 flex h-[48px] w-[48px] items-center justify-center justify-self-end rounded-full transition-all"
              >
                <PlusIcon />
              </PlainButton>
            );
          }}
        >
          {t({
            id: "crm-account-tile.connect-account",
            defaultMessage: "Connect Account",
          })}
        </Tooltip>
      </div>
      {buttonProps && (
        <>
          <div className="bg-dark-100 mx-1 h-10 w-px shrink-0" />
          <Button className="h-10" {...buttonProps}>
            {buttonProps.text}
          </Button>
        </>
      )}
      <div className="absolute bottom-10">
        {isConnected && status && <CRMStatusPill status={status} />}
      </div>
      {isConnected && menuItems && (
        <Menu
          placement="bottom"
          renderTrigger={({ Trigger, triggerRef }) => (
            <Trigger
              ref={triggerRef}
              as={PlainButton}
              className="bg-grey-100 hover:bg-grey-200 absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <MenuIcon />
            </Trigger>
          )}
          autoClose
          sections={menuItems}
        />
      )}
    </InteractiveContainer>
  );
};
